import { defineComponent as _defineComponent } from 'vue'
import { vModelText as _vModelText, createElementVNode as _createElementVNode, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "space-y-2" }
const _hoisted_2 = { class: "relative" }
const _hoisted_3 = {
  key: 0,
  class: "absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
}
const _hoisted_4 = {
  key: 0,
  class: "px-4 py-3 text-center"
}
const _hoisted_5 = { key: 1 }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { class: "font-medium text-black" }
const _hoisted_8 = { class: "text-sm text-black" }
const _hoisted_9 = { class: "text-xs text-black" }
const _hoisted_10 = {
  key: 2,
  class: "px-4 py-3 text-center text-black"
}
const _hoisted_11 = {
  key: 0,
  class: "flex flex-wrap gap-2"
}
const _hoisted_12 = { class: "text-sm mr-1" }
const _hoisted_13 = ["onClick"]
const _hoisted_14 = {
  key: 0,
  class: "flex items-center justify-between p-3 bg-gray-50 border border-gray-300 rounded-md"
}
const _hoisted_15 = { class: "font-medium text-black" }
const _hoisted_16 = { class: "text-sm text-black" }
const _hoisted_17 = { class: "text-xs text-black" }

import { ref, onMounted, onUnmounted } from 'vue'
import { searchBuyersForBlocking } from '@/api'
import type { BuyerAutocomplete } from '@/types'

interface Props {
  modelValue?: number | number[] | null
  multiple?: boolean
}

interface Emits {
  (e: 'update:modelValue', value: number | number[] | null): void
}


export default /*@__PURE__*/_defineComponent({
  __name: 'BuyerAutocompleteForBlocking',
  props: {
    modelValue: {},
    multiple: { type: Boolean, default: false }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props
const emit = __emit

// État local
const searchTerm = ref('')
const suggestions = ref<BuyerAutocomplete[]>([])
const showDropdown = ref(false)
const selectedBuyer = ref<BuyerAutocomplete | null>(null)
const selectedBuyers = ref<BuyerAutocomplete[]>([])
const isLoading = ref(false)
const searchTimeout = ref<number | null>(null)

// Charger les acheteurs lors de la recherche avec debounce
const searchBuyers = async (term: string) => {
  if (isLoading.value || term.length < 1) return
  
  isLoading.value = true
  try {
    suggestions.value = await searchBuyersForBlocking(term)
  } catch (error) {
    suggestions.value = []
  } finally {
    isLoading.value = false
  }
}

// Gestionnaires d'événements
const handleInput = () => {
  // Réinitialiser la sélection
  if (!props.multiple) {
    if (selectedBuyer.value) {
      selectedBuyer.value = null
      emit('update:modelValue', null)
    }
  }
  
  // Annuler le timeout précédent
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value)
  }
  
  if (searchTerm.value.length >= 1) {
    // Debounce de 300ms pour éviter trop d'appels API
    searchTimeout.value = setTimeout(() => {
      searchBuyers(searchTerm.value)
      showDropdown.value = true
    }, 300)
  } else {
    suggestions.value = []
    showDropdown.value = false
  }
}

const handleFocus = () => {
  if (searchTerm.value.length >= 1 && suggestions.value.length > 0) {
    showDropdown.value = true
  }
}

const handleBlur = () => {
  // Délai pour permettre le clic sur un élément
  setTimeout(() => {
    showDropdown.value = false
  }, 200)
}

const selectBuyer = (buyer: BuyerAutocomplete) => {
  if (props.multiple) {
    // Prevent duplicates
    if (!selectedBuyers.value.find(b => b.id === buyer.id)) {
      selectedBuyers.value.push(buyer)
      emit('update:modelValue', selectedBuyers.value.map(b => b.id))
    }
    searchTerm.value = ''
    suggestions.value = []
    showDropdown.value = false
  } else {
    selectedBuyer.value = buyer
    searchTerm.value = ''
    suggestions.value = []
    showDropdown.value = false
    emit('update:modelValue', buyer.id)
  }
}

const removeBuyer = (id: number) => {
  selectedBuyers.value = selectedBuyers.value.filter(b => b.id !== id)
  // In multiple mode, always emit an array (empty array when none selected)
  emit('update:modelValue', selectedBuyers.value.map(b => b.id))
}

const clearSelection = () => {
  if (props.multiple) {
    selectedBuyers.value = []
    searchTerm.value = ''
    suggestions.value = []
    // Emit empty array to keep parent model type stable (number[])
    emit('update:modelValue', [])
  } else {
    selectedBuyer.value = null
    searchTerm.value = ''
    suggestions.value = []
    emit('update:modelValue', null)
  }
}

// Fermer le dropdown en cliquant ailleurs
const handleClickOutside = (event: Event) => {
  const target = event.target as HTMLElement
  if (!target.closest('.relative')) {
    showDropdown.value = false
  }
}

// Lifecycle
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  // Nettoyer le timeout si le composant est démonté
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value)
  }
})

// Exposer la fonction de nettoyage
__expose({
  clearSelection
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _withDirectives(_createElementVNode("input", {
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchTerm).value = $event)),
        type: "text",
        class: "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent",
        placeholder: "Rechercher un acheteur...",
        onInput: handleInput,
        onFocus: handleFocus,
        onBlur: handleBlur
      }, null, 544), [
        [
          _vModelText,
          searchTerm.value,
          void 0,
          { trim: true }
        ]
      ]),
      (showDropdown.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            (isLoading.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, _cache[1] || (_cache[1] = [
                  _createElementVNode("div", { class: "flex items-center justify-center space-x-2" }, [
                    _createElementVNode("div", { class: "animate-spin rounded-full h-4 w-4 border-b-2 border-red-500" }),
                    _createElementVNode("span", { class: "text-sm text-black" }, "Recherche en cours...")
                  ], -1)
                ])))
              : (suggestions.value.length > 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(suggestions.value, (suggestion) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: suggestion.id,
                        onClick: ($event: any) => (selectBuyer(suggestion)),
                        class: "px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                      }, [
                        _createElementVNode("div", _hoisted_7, _toDisplayString(suggestion.label), 1),
                        _createElementVNode("div", _hoisted_8, _toDisplayString(suggestion.email), 1),
                        _createElementVNode("div", _hoisted_9, _toDisplayString(suggestion.desc), 1)
                      ], 8, _hoisted_6))
                    }), 128))
                  ]))
                : (!isLoading.value && searchTerm.value.length > 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_10, " Aucun acheteur trouvé "))
                  : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true)
    ]),
    (_ctx.multiple)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          (selectedBuyers.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedBuyers.value, (buyer) => {
                  return (_openBlock(), _createElementBlock("span", {
                    key: buyer.id,
                    class: "inline-flex items-center bg-gray-100 text-black px-2 py-1 rounded-full border border-gray-300"
                  }, [
                    _createElementVNode("span", _hoisted_12, _toDisplayString(buyer.label), 1),
                    _createElementVNode("button", {
                      type: "button",
                      class: "text-black hover:text-black",
                      onClick: ($event: any) => (removeBuyer(buyer.id))
                    }, _cache[2] || (_cache[2] = [
                      _createElementVNode("i", { class: "fas fa-times" }, null, -1)
                    ]), 8, _hoisted_13)
                  ]))
                }), 128))
              ]))
            : _createCommentVNode("", true)
        ], 64))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          (selectedBuyer.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                _createElementVNode("div", null, [
                  _createElementVNode("div", _hoisted_15, _toDisplayString(selectedBuyer.value.label), 1),
                  _createElementVNode("div", _hoisted_16, _toDisplayString(selectedBuyer.value.email), 1),
                  _createElementVNode("div", _hoisted_17, _toDisplayString(selectedBuyer.value.desc), 1)
                ]),
                _createElementVNode("button", {
                  type: "button",
                  onClick: clearSelection,
                  class: "text-black hover:text-black"
                }, _cache[3] || (_cache[3] = [
                  _createElementVNode("i", { class: "fas fa-times" }, null, -1)
                ]))
              ]))
            : _createCommentVNode("", true)
        ], 64))
  ]))
}
}

})